import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import type { RunDriveBackupInput, RunDriveBackupResult } from '@citizen-walk/shared-types';
import { functions } from '../../lib/firebase';

/**
 * runDriveBackup callable hook (Sprint BB Part 2)。
 *
 * useExportPosts と同 pattern (idle / pending / success / error 4 状態)。
 * 結果の files は signed URL list、admin は browser から直接 download (window.open) or
 * 表示済 link をクリックして個別 DL。
 */

export type UseDriveBackupState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: RunDriveBackupResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseDriveBackupReturn {
  state: UseDriveBackupState;
  call: (input: RunDriveBackupInput) => Promise<RunDriveBackupResult | null>;
  reset: () => void;
}

export function useDriveBackup(): UseDriveBackupReturn {
  const [state, setState] = useState<UseDriveBackupState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: RunDriveBackupInput): Promise<RunDriveBackupResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<RunDriveBackupInput, RunDriveBackupResult>(
        functions,
        'runDriveBackup',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}
